import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { PrintedCounterBadge } from "../components/PrintedCounterBadge";
import { PrintedWall } from "../components/PrintedWall";
import { SessionGallery } from "../components/SessionGallery";
import { StudioStage } from "../components/StudioStage";
import { StudioGateFooter } from "../components/StudioGateFooter";
import { SpendMeter } from "../components/SpendMeter";
import { BuildVersion } from "../components/BuildVersion";
import { useGallery } from "../hooks/useGallery";
import { useBuildPolling } from "../hooks/useBuildPolling";
import { useCreationActions } from "../hooks/useCreationActions";
import { useAuth } from "../context/AuthContext";
import { installQuackUnlock } from "../lib/quack";

const STEP_META = {
  1: { kicker: "Step 01 — Upload", title: "Turn any face into a rubber duck" },
  2: { kicker: "Step 02 — Approve", title: "Meet your new duck" },
  3: { kicker: "Step 03 — Print", title: "Sculpt & export" },
};

export default function Studio() {
  const [step, setStep] = useState(1);
  const [current, setCurrent] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const { gallery, total, loadGallery, loadMore, hasMore, loadingMore, patchGallery } = useGallery();
  useBuildPolling(current, setCurrent, loadGallery);
  const actions = useCreationActions({ current, setCurrent, setStep, loadGallery });
  const actionsRef = useRef(actions); // actions is rebuilt every render; the open-effect only needs the latest one
  actionsRef.current = actions;

  // Unlock the quack sound on the user's first interaction (autoplay policy), so later completion quacks can play.
  useEffect(() => { installQuackUnlock(); }, []);

  // When the signed-in user changes (login / logout), drop any open duck; useGallery re-fetches for the new user itself.
  const userId = user?.id || null;
  useEffect(() => {
    setCurrent(null);
    setStep(1);
    setGalleryOpen(false);
  }, [userId]);

  // A duck whose render is still running must survive a refresh: the id is remembered when it is created.
  const openId = params.get("open") || (userId ? localStorage.getItem("duckify_current_duck") : null);
  useEffect(() => {
    if (!openId) return;
    setParams({}, { replace: true });
    actionsRef.current.openItem({ id: openId });
    try { localStorage.removeItem("duckify_current_duck"); } catch { /* private mode */ }
  }, [openId, setParams]);

  const goStep = (n) => {
    if (n === 1) setStep(1);
    else if (n <= step && current) setStep(n);
  };
  const meta = STEP_META[step];

  return (
    <div className="App min-h-screen bg-dots">
      <Navbar step={step} onStepClick={goStep} galleryCount={total} onOpenGallery={() => setGalleryOpen(true)} />

      <main className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-10 sm:py-14">
        <div className="mb-9">
          <span className="text-xs font-mono2 uppercase tracking-widest text-accent font-semibold">{meta.kicker}</span>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-2">{meta.title}</h1>
          {step === 1 && <PrintedCounterBadge />}
          {step === 1 && <PrintedWall />}
        </div>
        <StudioStage step={step} current={current} actions={actions} loadGallery={loadGallery} setStep={setStep} />
        <SpendMeter variant="line" />
        <StudioGateFooter />
        <BuildVersion />
      </main>

      <SessionGallery
        open={galleryOpen}
        onOpenChange={setGalleryOpen}
        items={gallery}
        total={total}
        hasMore={hasMore}
        loadingMore={loadingMore}
        onLoadMore={loadMore}
        onOpenItem={(c) => {
          setGalleryOpen(false);
          actions.openItem(c);
        }}
        onDelete={actions.deleteItem}
        onClear={actions.clearGallery}
        onReload={loadGallery}
        onRenamed={patchGallery}
      />
    </div>
  );
}
